import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';

// ── Email the whole custody chain to the client (owner 2026-08-29) ──────────────────────────────
// "当出现和客户争议时" — one button on the chain, one email carrying every leg in order, laid out
// like the block on screen. The server prepares it (To = the client, Cc = office@samuilawfirm.com
// + the firm's own office); this composer is the human review: recipients, subject and the body
// itself are all editable before anything is sent.

const inp: React.CSSProperties = {
  width: '100%', padding: '8px 10px', fontSize: 13, borderRadius: 8,
  border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)', outline: 'none', boxSizing: 'border-box',
};
const lbl: React.CSSProperties = { fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', alignSelf: 'center' };

export default function ChainEmail({ legId, onClose, onSent }: { legId: string; onClose: () => void; onSent: (to: string) => void }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState('');
  const [others, setOthers] = useState<string[]>([]);   // other client addresses on this chain
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [subject, setSubject] = useState('');
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const [html, setHtml] = useState('');   // the div is only mounted once loading is done

  useEffect(() => {
    api.trChainEmailPrepare(legId).then((p: any) => {
      setFrom(p.from || ''); setTo((p.to || []).join(', ')); setCc((p.cc || []).join(', '));
      setSubject(p.subject || ''); setOthers(p.otherEmails || []); setHtml(p.bodyHtml || '');
    }).catch((e: any) => setErr(e.message)).finally(() => setLoading(false));
  }, [legId]);

  // The editor only exists after `loading` flips, so the body is written in a second pass — setting
  // innerHTML inside the fetch callback landed on a ref that was still null and left it blank.
  useEffect(() => {
    if (!loading && bodyRef.current && html && !bodyRef.current.innerHTML) bodyRef.current.innerHTML = html;
  }, [loading, html]);

  const send = async () => {
    if (!to.trim()) { setErr(t('tr_ce_need_to')); return; }
    setSending(true); setErr('');
    try {
      const r: any = await api.trChainEmailSend(legId, { to, cc, subject, bodyHtml: bodyRef.current?.innerHTML || '' });
      onSent((r.to || []).join(', ')); onClose();
    } catch (e: any) { setErr(e.message); setSending(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 10120, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
      onMouseDown={e => { if (e.target === e.currentTarget && !sending) onClose(); }}>
      <div style={{ width: 860, maxWidth: '100%', maxHeight: '92vh', display: 'flex', flexDirection: 'column', background: 'var(--bg-surface)', borderRadius: 14, overflow: 'hidden', boxShadow: 'var(--shadow-md)' }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', fontSize: 15, fontWeight: 800 }}>
          <i className="ti ti-mail-forward" style={{ marginRight: 8, color: 'var(--accent)' }} aria-hidden />{t('tr_ce_title')}
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '14px 20px' }}>
          {loading ? <div style={{ fontSize: 13, color: 'var(--text-tertiary)' }}>{t('loading')}</div> : (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '58px 1fr', gap: '8px 10px', marginBottom: 12 }}>
                <span style={lbl}>{t('tr_ce_from')}</span><span style={{ fontSize: 13, alignSelf: 'center', color: 'var(--text-secondary)' }}>{from}</span>
                <span style={lbl}>{t('tr_ce_to')}</span><input value={to} onChange={e => setTo(e.target.value)} style={inp} />
                <span style={lbl}>{t('tr_ce_cc')}</span><input value={cc} onChange={e => setCc(e.target.value)} style={inp} />
                <span style={lbl}>{t('tr_ce_subject')}</span><input value={subject} onChange={e => setSubject(e.target.value)} style={inp} />
              </div>
              {/* Legs of one chain often carry different client addresses (they are typed per leg).
                  The default is the newest real one; the others are shown, never auto-added. */}
              {others.length > 0 && (
                <div style={{ fontSize: 12, color: '#B45309', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, padding: '8px 11px', marginBottom: 10 }}>
                  <i className="ti ti-alert-triangle" style={{ marginRight: 6 }} aria-hidden />{t('tr_ce_other_emails')} {others.join(', ')}
                </div>
              )}
              {/* The body is the chain, rendered as it will arrive. Editable — a dispute email often
                  needs a sentence of context on top of the record. */}
              <div ref={bodyRef} contentEditable suppressContentEditableWarning
                style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '14px 18px', background: '#fff', color: '#111', minHeight: 260, outline: 'none', fontSize: 14 }} />
            </>
          )}
          {err && <div style={{ marginTop: 10, fontSize: 12.5, color: '#B42318', background: '#FEF3F2', border: '1px solid #FECDCA', borderRadius: 8, padding: '8px 12px' }}>{err}</div>}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '12px 20px', borderTop: '1px solid var(--border)' }}>
          <button onClick={onClose} disabled={sending} style={{ padding: '9px 18px', borderRadius: 9, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', fontSize: 13, cursor: 'pointer' }}>{t('cancel')}</button>
          <button onClick={send} disabled={sending || loading} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 20px', borderRadius: 9, border: 'none', background: 'var(--accent)', color: '#fff', fontWeight: 700, fontSize: 13, cursor: sending ? 'default' : 'pointer', opacity: sending ? 0.7 : 1 }}>
            <i className={`ti ${sending ? 'ti-loader-2' : 'ti-send'}`} style={{ animation: sending ? 'spin .8s linear infinite' : undefined, display: 'inline-block' }} />
            {sending ? t('tr_ce_sending') : t('tr_ce_send')}
          </button>
        </div>
      </div>
    </div>
  );
}
